import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { LogoPrimary } from '@/components/Logo';
import {
  Search, Crown, ArrowRight, Swords, Gamepad2, Trophy,
  Loader2, Eye, EyeOff, ChevronRight, Zap, Shield,
  Users, Star, Clock, TrendingUp, Radio, Target,
  MessageCircle, Bell, User, Menu, X, Globe, FileText,
  HelpCircle, Info, Mail, AlertTriangle, Wallet, CircleDollarSign,
  CheckCircle2, UserPlus, Crosshair, DollarSign
} from 'lucide-react';
import { BlishAPI } from '@/apis';
import { useStore } from '@/store/useStore';
import { decryptResponse } from '@/lib/utils';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ─────────────────────────────────────────────
   CAROUSEL IMAGES — auto-scrolling background
   ───────────────────────────────────────────── */
const CAROUSEL_IMAGES = [
  { url: 'https://images.unsplash.com/photo-1775809901490-1b0b85d8578c?w=1400&q=70&auto=format', label: 'Console Gaming' },
  { url: 'https://images.unsplash.com/photo-1766051666522-9cfa12675f5b?w=1400&q=70&auto=format', label: 'PlayStation' },
  { url: 'https://images.unsplash.com/photo-1593305841991-05c297ba4575?w=1400&q=70&auto=format', label: 'Xbox' },
  { url: 'https://images.unsplash.com/photo-1679456690102-f9fe4a195029?w=1400&q=70&auto=format', label: 'Controller' },
  { url: 'https://images.unsplash.com/photo-1699962700191-0f5633845733?w=1400&q=70&auto=format', label: 'Esports' },
  { url: 'https://images.unsplash.com/photo-1763258986433-0fbb73a7d574?w=1400&q=70&auto=format', label: 'Competitive' },
];




/* ─────────────────────────────────────────────
   RESEND VERIFICATION — cooldown + feedback
   ───────────────────────────────────────────── */
const RESEND_COOLDOWN = 60; // seconds

const ResendVerification = ({ email }) => {
  const [cooldown, setCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const timerRef = useRef(null);

  // Clean up timer on unmount
  useEffect(() => () => clearInterval(timerRef.current), []);

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN);
    timerRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (cooldown > 0 || resendLoading) return;
    setResendLoading(true);
    try {
      await BlishAPI.auth.resendVerification(email);
      toast.success('Verification code resent! Check your inbox.');
      startCooldown();
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || 'Failed to resend. Try again.';
      // Handle the specific 400 case — already verified
      if (err.response?.status === 400) {
        toast.info('This email is already verified. Try logging in.');
      } else {
        toast.error(msg);
      }
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="mt-4 text-center">
      <p className="text-xs text-gray-500 mb-2">Didn't receive a code?</p>
      <button
        type="button"
        onClick={handleResend}
        disabled={cooldown > 0 || resendLoading}
        data-testid="auth-resend-btn"
        className={`inline-flex items-center gap-1.5 text-xs font-semibold transition-all px-3 py-1.5 rounded-lg border ${
          cooldown > 0 || resendLoading
            ? 'text-gray-600 border-white/5 bg-white/[0.02] cursor-not-allowed'
            : 'text-[#1EF2FF] border-[#1EF2FF]/20 bg-[#1EF2FF]/5 hover:bg-[#1EF2FF]/10 active:scale-95'
        }`}
      >
        {resendLoading ? (
          <><Loader2 className="w-3 h-3 animate-spin" />Sending...</>
        ) : cooldown > 0 ? (
          <><Clock className="w-3 h-3" />Resend in {cooldown}s</>
        ) : (
          <><ArrowRight className="w-3 h-3 rotate-[-90deg]" />Resend Code</>
        )}
      </button>
    </div>
  );
};
/* ─────────────────────────────────────────────
   BACKGROUND CAROUSEL — subtle auto-crossfade
   ───────────────────────────────────────────── */
const BackgroundCarousel = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx(p => (p + 1) % CAROUSEL_IMAGES.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="absolute inset-0 z-0 overflow-hidden" aria-hidden="true">
      {CAROUSEL_IMAGES.map((img, i) => (
        <div
          key={i}
          className="absolute inset-0 bg-cover bg-center transition-opacity duration-[2500ms] ease-in-out will-change-[opacity]"
          style={{
            backgroundImage: `url(${img.url})`,
            opacity: i === idx ? 0.18 : 0,
          }}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0D0E13]/92 via-[#0D0E13]/88 to-[#0D0E13]/95" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0D0E13]/60 via-transparent to-[#0D0E13]/60" />
      <motion.div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none"
        animate={{ scale: [1, 1.08, 1], rotate: [0, 3, -3, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      >
        <Crown className="w-64 h-64 md:w-96 md:h-96 text-[#FFD700] opacity-[0.025]" />
      </motion.div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   TRUST BAR
   ───────────────────────────────────────────── */
const TrustBar = () => (
  <div data-testid="trust-bar" className="flex-shrink-0 w-full bg-[#FFD700]/5 border-b border-[#FFD700]/10 py-2 px-4 z-40">
    <div className="flex items-center justify-center gap-2 sm:gap-6 text-[10px] sm:text-xs font-semibold tracking-wide text-[#FFD700]/80 whitespace-nowrap overflow-x-auto hide-scrollbar">
      <span className="flex items-center gap-1.5"><Shield className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#FFD700]" />Skill-Based Only</span>
      <span className="text-[#FFD700]/30">|</span>
      <span className="flex items-center gap-1.5"><Trophy className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#FFD700]" />No Pay-to-Win</span>
      <span className="text-[#FFD700]/30">|</span>
      <span className="flex items-center gap-1.5"><Users className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#FFD700]" />Backed by Real Gamers</span>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   HOW IT WORKS — 4-step premium section
   ───────────────────────────────────────────── */
const HOW_STEPS = [
  { icon: UserPlus, title: 'Sign Up', desc: 'Create your free account and set your gamertag in seconds.', color: 'text-[#1EF2FF]', bg: 'bg-[#1EF2FF]/10', border: 'border-[#1EF2FF]/20' },
  { icon: Crosshair, title: 'Find a Match', desc: 'Browse open challenges or create your own wager.', color: 'text-[#9B45FF]', bg: 'bg-[#9B45FF]/10', border: 'border-[#9B45FF]/20' },
  { icon: Swords, title: 'Compete', desc: 'Play your match on console or PC. Submit scores.', color: 'text-[#FFD700]', bg: 'bg-[#FFD700]/10', border: 'border-[#FFD700]/20' },
  { icon: Crown, title: 'Get Blish Coin', desc: 'Winner takes the pot. Cash out or flex your BC.', color: 'text-green-400', bg: 'bg-green-400/10', border: 'border-green-400/20' },
];

const HowItWorks = () => (
  <div data-testid="how-it-works-section" className="w-full max-w-md lg:max-w-none">
    <div className="flex items-center justify-center gap-2 mb-4">
      <div className="h-px flex-1 bg-gradient-to-r from-transparent to-white/10" />
      <h3 className="text-xs sm:text-sm font-bold text-gray-300 uppercase tracking-widest whitespace-nowrap">How It Works</h3>
      <div className="h-px flex-1 bg-gradient-to-l from-transparent to-white/10" />
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
      {HOW_STEPS.map((step, i) => (
        <motion.div
          key={step.title}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 + i * 0.1, duration: 0.4 }}
          className={`relative ${step.bg} border ${step.border} rounded-xl p-3 sm:p-4 text-center group hover:scale-[1.02] transition-transform`}
        >
          <div className="absolute -top-2 -left-1 w-5 h-5 rounded-full bg-[#0D0E13] border border-white/10 flex items-center justify-center">
            <span className="text-[9px] font-black text-gray-400">{i + 1}</span>
          </div>
          <step.icon className={`w-6 h-6 sm:w-7 sm:h-7 mx-auto mb-2 ${step.color}`} />
          <p className="text-xs sm:text-sm font-bold text-white mb-0.5">{step.title}</p>
          <p className="text-[10px] sm:text-xs text-gray-500 leading-snug">{step.desc}</p>
        </motion.div>
      ))}
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   MOBILE BOTTOM NAV — Instagram-style pre-login
   ───────────────────────────────────────────── */
const LANDING_TABS = [
  { key: 'home', icon: Crown, label: 'Home', color: 'text-[#FFD700]' },
  { key: 'live', icon: Radio, label: 'Live', color: 'text-red-400' },
  { key: 'play', icon: Gamepad2, label: 'Play', color: 'text-[#9B45FF]' },
  { key: 'battles', icon: Swords, label: 'Battles', color: 'text-orange-400' },
  { key: 'chat', icon: MessageCircle, label: 'Chat', color: 'text-green-400' },
  { key: 'menu', icon: Menu, label: 'Menu', color: 'text-gray-400' },
];

const MobileBottomNav = ({ onTabClick, onMenuClick, activeTab }) => (
  <nav data-testid="landing-mobile-bottom-nav" className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#0D0E13]/95 backdrop-blur-xl border-t border-white/10 flex items-center justify-around px-1 pt-2 pb-5 z-[100]">
    {LANDING_TABS.map((tab) => {
      const isActive = activeTab === tab.key;
      const isMenu = tab.key === 'menu';
      return (
        <button
          key={tab.key}
          onClick={() => isMenu ? onMenuClick() : onTabClick(tab.key)}
          data-testid={`landing-tab-${tab.key}`}
          className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-all min-w-[48px] active:scale-90 ${isActive ? 'text-white' : 'text-gray-600'}`}
        >
          <tab.icon className={`w-5 h-5 ${isActive ? tab.color : ''}`} />
          <span className="text-[9px] font-semibold">{tab.label}</span>
        </button>
      );
    })}
  </nav>
);

/* ─────────────────────────────────────────────
   MOBILE MENU OVERLAY
   ───────────────────────────────────────────── */
const MobileMenuOverlay = ({ show, onClose, onNavClick }) => {
  const APP_SECTIONS = [
    { key: 'dashboard', icon: Crown, label: 'Dashboard', color: 'text-[#FFD700]' },
    { key: 'live', icon: Radio, label: 'Live & Stream', color: 'text-red-400' },
    { key: 'tournaments', icon: Gamepad2, label: 'Tournaments', color: 'text-[#9B45FF]' },
    { key: 'matchmaking', icon: Target, label: 'Matchmaking', color: 'text-[#1EF2FF]' },
    { key: 'challenges', icon: Swords, label: 'Challenges', color: 'text-orange-400' },
    { key: 'messages', icon: MessageCircle, label: 'Messages', color: 'text-green-400' },
    { key: 'notifications', icon: Bell, label: 'Notifications', color: 'text-yellow-400' },
    { key: 'profile', icon: User, label: 'Profile', color: 'text-[#1EF2FF]' },
  ];

  const FOOTER_LINKS = [
    { to: '/features', icon: Star, label: 'Features' },
    { to: '/how-it-works', icon: Info, label: 'How It Works' },
    { to: '/faq', icon: HelpCircle, label: 'FAQ' },
    { to: '/about', icon: Globe, label: 'About' },
    { to: '/contact', icon: Mail, label: 'Contact' },
    { to: '/terms', icon: FileText, label: 'Terms' },
    { to: '/privacy', icon: Shield, label: 'Privacy' },
    { to: '/risk-disclosure', icon: AlertTriangle, label: 'Risk Disclosure' },
  ];

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="lg:hidden fixed inset-0 z-[150] bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="absolute bottom-0 left-0 right-0 bg-[#1A1B22] border-t border-white/10 rounded-t-2xl max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[#1A1B22] z-10 px-4 pt-3 pb-2">
              <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-3" />
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-white uppercase tracking-wider">Explore Blishcrown</h3>
                <button onClick={onClose} className="text-gray-500 hover:text-white p-1"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="px-4 pb-3">
              <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-2 font-semibold">Platform</p>
              <div className="grid grid-cols-4 gap-2">
                {APP_SECTIONS.map(item => (
                  <button
                    key={item.key}
                    onClick={() => { onNavClick(item.key); onClose(); }}
                    data-testid={`menu-section-${item.key}`}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all active:scale-95"
                  >
                    <item.icon className={`w-5 h-5 ${item.color}`} />
                    <span className="text-[10px] text-gray-300 font-medium text-center leading-tight">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="px-4 pb-6 pt-2 border-t border-white/5">
              <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-2 font-semibold">Info & Legal</p>
              <div className="grid grid-cols-2 gap-1">
                {FOOTER_LINKS.map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={onClose}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-all"
                  >
                    <link.icon className="w-4 h-4 text-gray-500" />
                    <span className="text-xs text-gray-400 font-medium">{link.label}</span>
                  </Link>
                ))}
              </div>
              <a href="https://discord.gg/Tbca3Hvd4w" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-all mt-1">
                <Globe className="w-4 h-4 text-[#5865F2]" />
                <span className="text-xs text-gray-400 font-medium">Discord Community</span>
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ─────────────────────────────────────────────
   HELPER FUNCTIONS
   ───────────────────────────────────────────── */
const GoogleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const getRankColor = (rank) => {
  const r = (rank || '').toLowerCase();
  if (r === 'legend') return 'text-[#FFD700]';
  if (r === 'elite') return 'text-[#9B45FF]';
  if (r === 'pro') return 'text-[#8B5CF6]';
  if (r === 'amateur') return 'text-[#1EF2FF]';
  return 'text-gray-400';
};

const getRankBorder = (rank) => {
  const r = (rank || '').toLowerCase();
  if (r === 'legend') return 'ring-[#FFD700]';
  if (r === 'elite') return 'ring-[#9B45FF]';
  if (r === 'pro') return 'ring-[#8B5CF6]';
  if (r === 'amateur') return 'ring-[#1EF2FF]';
  return 'ring-gray-600';
};

const getGameAccent = (game) => {
  const g = (game || '').toLowerCase();
  if (g.includes('fifa')) return 'border-l-green-500';
  if (g.includes('call') || g.includes('cod')) return 'border-l-orange-500';
  if (g.includes('nba') || g.includes('2k')) return 'border-l-blue-500';
  if (g.includes('madden')) return 'border-l-red-500';
  return 'border-l-[#9B45FF]';
};

const getGameGlow = (game) => {
  const g = (game || '').toLowerCase();
  if (g.includes('fifa')) return 'shadow-green-500/5';
  if (g.includes('call') || g.includes('cod')) return 'shadow-orange-500/5';
  if (g.includes('nba') || g.includes('2k')) return 'shadow-blue-500/5';
  if (g.includes('madden')) return 'shadow-red-500/5';
  return 'shadow-purple-500/5';
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

/* Clean display for gamertags — replaces test/placeholder names */
const displayTag = (tag) => {
  if (!tag) return 'Challenger';
  const lower = tag.toLowerCase();
  if (lower === 'unknown' || lower.includes('newbie') || lower === 'test' || lower.length <= 2) return 'Challenger';
  return tag;
};

const displayRank = (rank) => {
  if (!rank || rank.toLowerCase() === 'unknown') return 'Rookie';
  return rank;
};

/* ─────────────────────────────────────────────
   MOCK DATA — Professional gamertags/content
   ───────────────────────────────────────────── */
const MOCK_CHALLENGES = [
  { id: '1', game: 'FIFA 25', amount: 50, mode: '1v1', platform: 'PS5', creator_gamertag: 'ShadowKing', creator_rank: 'Pro', created_at: new Date(Date.now() - 300000).toISOString() },
  { id: '2', game: 'Call of Duty', amount: 100, mode: 'Gunfight', platform: 'Xbox', creator_gamertag: 'NeonSlayer', creator_rank: 'Elite', created_at: new Date(Date.now() - 600000).toISOString() },
  { id: '3', game: 'NBA 2K25', amount: 25, mode: '1v1', platform: 'PS5', creator_gamertag: 'CourtVision', creator_rank: 'Amateur', created_at: new Date(Date.now() - 900000).toISOString() },
  { id: '4', game: 'Madden 25', amount: 75, mode: 'H2H', platform: 'Xbox', creator_gamertag: 'GridironGhost', creator_rank: 'Pro', created_at: new Date(Date.now() - 1200000).toISOString() },
  { id: '5', game: 'FIFA 25', amount: 200, mode: '1v1', platform: 'PC', creator_gamertag: 'GoldenBoot', creator_rank: 'Legend', created_at: new Date(Date.now() - 1800000).toISOString() },
  { id: '6', game: 'Call of Duty', amount: 30, mode: 'Search & Destroy', platform: 'PS5', creator_gamertag: 'TacticalAce', creator_rank: 'Amateur', created_at: new Date(Date.now() - 2400000).toISOString() },
  { id: '7', game: 'NBA 2K25', amount: 150, mode: 'Park', platform: 'Xbox', creator_gamertag: 'DunkMaster', creator_rank: 'Elite', created_at: new Date(Date.now() - 3000000).toISOString() },
  { id: '8', game: 'FIFA 25', amount: 40, mode: '1v1', platform: 'PS5', creator_gamertag: 'SkillMoves', creator_rank: 'Pro', created_at: new Date(Date.now() - 3600000).toISOString() },
  { id: '9', game: 'Madden 25', amount: 60, mode: 'Franchise', platform: 'PC', creator_gamertag: 'BlitzKing', creator_rank: 'Amateur', created_at: new Date(Date.now() - 4800000).toISOString() },
  { id: '10', game: 'Call of Duty', amount: 500, mode: 'Warzone', platform: 'PC', creator_gamertag: 'WarChief', creator_rank: 'Legend', created_at: new Date(Date.now() - 7200000).toISOString() },
  { id: '11', game: 'NBA 2K25', amount: 80, mode: '1v1', platform: 'PS5', creator_gamertag: 'CrossKing', creator_rank: 'Pro', created_at: new Date(Date.now() - 9000000).toISOString() },
  { id: '12', game: 'FIFA 25', amount: 120, mode: 'Pro Clubs', platform: 'Xbox', creator_gamertag: 'GoalMachine', creator_rank: 'Elite', created_at: new Date(Date.now() - 10800000).toISOString() },
];

const MOCK_MATCHUPS = [
  { id: '1', game: 'FIFA 25', amount: 100, status: 'active', player_a: { gamertag: 'ProKing_X', rank: 'Legend' }, player_b: { gamertag: 'ShadowStrike', rank: 'Elite' }, created_at: new Date(Date.now() - 120000).toISOString() },
  { id: '2', game: 'Call of Duty', amount: 50, status: 'completed', winner_id: 'a', player_a: { gamertag: 'NeonSlayer', rank: 'Pro' }, player_b: { gamertag: 'TacticalAce', rank: 'Amateur' }, created_at: new Date(Date.now() - 300000).toISOString() },
  { id: '3', game: 'NBA 2K25', amount: 200, status: 'active', player_a: { gamertag: 'CourtVision', rank: 'Elite' }, player_b: { gamertag: 'DunkMaster', rank: 'Pro' }, created_at: new Date(Date.now() - 600000).toISOString() },
  { id: '4', game: 'Madden 25', amount: 75, status: 'completed', winner_id: 'b', player_a: { gamertag: 'GridironGhost', rank: 'Amateur' }, player_b: { gamertag: 'BlitzKing', rank: 'Pro' }, created_at: new Date(Date.now() - 900000).toISOString() },
  { id: '5', game: 'FIFA 25', amount: 300, status: 'active', player_a: { gamertag: 'GoldenBoot', rank: 'Legend' }, player_b: { gamertag: 'SkillMoves', rank: 'Elite' }, created_at: new Date(Date.now() - 1200000).toISOString() },
  { id: '6', game: 'Call of Duty', amount: 150, status: 'pending_result', player_a: { gamertag: 'WarChief', rank: 'Legend' }, player_b: { gamertag: 'NeonSlayer', rank: 'Pro' }, created_at: new Date(Date.now() - 1800000).toISOString() },
  { id: '7', game: 'NBA 2K25', amount: 50, status: 'completed', winner_id: 'a', player_a: { gamertag: 'CrossKing', rank: 'Pro' }, player_b: { gamertag: 'CourtVision', rank: 'Amateur' }, created_at: new Date(Date.now() - 2400000).toISOString() },
  { id: '8', game: 'FIFA 25', amount: 80, status: 'active', player_a: { gamertag: 'GoalMachine', rank: 'Elite' }, player_b: { gamertag: 'ProKing_X', rank: 'Legend' }, created_at: new Date(Date.now() - 3600000).toISOString() },
  { id: '9', game: 'Madden 25', amount: 100, status: 'completed', winner_id: 'a', player_a: { gamertag: 'BlitzKing', rank: 'Pro' }, player_b: { gamertag: 'GridironGhost', rank: 'Amateur' }, created_at: new Date(Date.now() - 5400000).toISOString() },
  { id: '10', game: 'Call of Duty', amount: 250, status: 'active', player_a: { gamertag: 'TacticalAce', rank: 'Elite' }, player_b: { gamertag: 'ShadowStrike', rank: 'Pro' }, created_at: new Date(Date.now() - 7200000).toISOString() },
  { id: '11', game: 'NBA 2K25', amount: 175, status: 'pending_result', player_a: { gamertag: 'DunkMaster', rank: 'Legend' }, player_b: { gamertag: 'CrossKing', rank: 'Elite' }, created_at: new Date(Date.now() - 9000000).toISOString() },
  { id: '12', game: 'FIFA 25', amount: 60, status: 'completed', winner_id: 'b', player_a: { gamertag: 'SkillMoves', rank: 'Pro' }, player_b: { gamertag: 'GoalMachine', rank: 'Elite' }, created_at: new Date(Date.now() - 10800000).toISOString() },
];

/* ─────────────────────────────────────────────
   CHALLENGE CARD — Premium upgrade
   ───────────────────────────────────────────── */
const ChallengeCard = ({ challenge, index, onAction }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * 0.04 }}
    onClick={onAction}
    data-testid={`challenge-card-${index}`}
    className={`bg-[#1A1B22]/70 backdrop-blur-sm border border-white/[0.06] border-l-[3px] ${getGameAccent(challenge.game)} rounded-r-xl p-3.5 sm:p-4 hover:-translate-y-0.5 hover:bg-[#1A1B22]/90 hover:shadow-lg ${getGameGlow(challenge.game)} transition-all cursor-pointer group`}
  >
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-1.5">
        <Gamepad2 className="w-3 h-3 text-gray-500" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{challenge.game}</span>
      </div>
      <span className="text-[10px] text-gray-500 flex items-center gap-1">
        <Clock className="w-2.5 h-2.5" />
        {timeAgo(challenge.created_at)}
      </span>
    </div>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className={`w-7 h-7 rounded-full bg-gray-700/80 ring-1 ${getRankBorder(challenge.creator_rank)} flex items-center justify-center text-[10px] font-bold text-white`}>
          {displayTag(challenge.creator_gamertag)[0].toUpperCase()}
        </div>
        <div>
          <span className={`text-sm font-bold ${getRankColor(challenge.creator_rank)}`}>{displayTag(challenge.creator_gamertag)}</span>
          <span className="text-[10px] text-gray-500 ml-1.5">{displayRank(challenge.creator_rank)}</span>
        </div>
      </div>
      <div className="text-right">
        <span className="text-[#FFD700] font-bold text-sm">{challenge.amount} BC</span>
      </div>
    </div>
    <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/[0.04]">
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-gray-500 bg-white/[0.04] px-1.5 py-0.5 rounded">{challenge.mode}</span>
        <span className="text-[10px] text-gray-500 bg-white/[0.04] px-1.5 py-0.5 rounded">{challenge.platform}</span>
      </div>
      <span className="text-[10px] text-[#1EF2FF] font-semibold uppercase opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
        Accept <ChevronRight className="w-3 h-3" />
      </span>
    </div>
  </motion.div>
);

/* ─────────────────────────────────────────────
   MATCHUP CARD — Premium upgrade with live glow
   ───────────────────────────────────────────── */
const MatchupCard = ({ matchup, index, onAction }) => {
  const isLive = matchup.status === 'active';
  const isComplete = matchup.status === 'completed';
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04 }}
      onClick={onAction}
      data-testid={`matchup-card-${index}`}
      className={`bg-[#1A1B22]/70 backdrop-blur-sm border rounded-xl p-3.5 sm:p-4 hover:-translate-y-0.5 transition-all cursor-pointer ${
        isLive
          ? 'border-red-500/20 shadow-[0_0_20px_rgba(239,68,68,0.06)]'
          : 'border-white/[0.06] hover:border-[#1EF2FF]/20'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <Gamepad2 className="w-3 h-3 text-gray-500" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{matchup.game}</span>
        </div>
        <div className="flex items-center gap-2">
          {isLive && (
            <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
              Live
            </span>
          )}
          {isComplete && (
            <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded-full">
              <CheckCircle2 className="w-2.5 h-2.5" />
              Done
            </span>
          )}
          {matchup.status === 'pending_result' && (
            <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-yellow-400 bg-yellow-500/10 px-1.5 py-0.5 rounded-full">
              <Clock className="w-2.5 h-2.5" />
              Judging
            </span>
          )}
          <span className="text-[#FFD700] font-bold text-xs">{matchup.amount} BC</span>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col items-center flex-1 min-w-0">
          <div className={`w-10 h-10 rounded-full bg-gray-700/80 ring-2 ${getRankBorder(matchup.player_a?.rank)} flex items-center justify-center text-sm font-bold text-white`}>
            {displayTag(matchup.player_a?.gamertag)[0].toUpperCase()}
          </div>
          <span className="text-xs font-semibold text-white mt-1 truncate max-w-full">{displayTag(matchup.player_a?.gamertag)}</span>
          <span className={`text-[10px] ${getRankColor(matchup.player_a?.rank)}`}>{displayRank(matchup.player_a?.rank)}</span>
        </div>
        <div className="flex-shrink-0 px-1">
          {isLive ? (
            <motion.span
              animate={{ scale: [1, 1.15, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-red-400 font-black text-xs"
              style={{ textShadow: '0 0 12px rgba(239,68,68,0.5)' }}
            >VS</motion.span>
          ) : (
            <span className="text-[#1EF2FF]/60 font-black text-xs">VS</span>
          )}
        </div>
        <div className="flex flex-col items-center flex-1 min-w-0">
          <div className={`w-10 h-10 rounded-full bg-gray-700/80 ring-2 ${getRankBorder(matchup.player_b?.rank)} flex items-center justify-center text-sm font-bold text-white`}>
            {displayTag(matchup.player_b?.gamertag)[0].toUpperCase()}
          </div>
          <span className="text-xs font-semibold text-white mt-1 truncate max-w-full">{displayTag(matchup.player_b?.gamertag)}</span>
          <span className={`text-[10px] ${getRankColor(matchup.player_b?.rank)}`}>{displayRank(matchup.player_b?.rank)}</span>
        </div>
      </div>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────
   INLINE SEARCH DROPDOWN — fast autocomplete
   Renders directly below the search input, no overlay
   ───────────────────────────────────────────── */
const SearchDropdown = ({ onAction }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced search
  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await BlishAPI.public.searchPlayer(query);
        setResults(res.data || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const winRate = (w, l) => {
    const total = (w || 0) + (l || 0);
    return total === 0 ? '—' : `${Math.round(((w || 0) / total) * 100)}%`;
  };

  const handleAction = (action, player) => {
    setOpen(false);
    setQuery('');
    setResults([]);
    onAction(action, player);
  };

  return (
    <div ref={wrapperRef} className="relative w-full" data-testid="search-dropdown-wrapper">
      <div className="flex items-center bg-white/5 border border-white/10 rounded-full px-4 py-2 focus-within:border-[#1EF2FF]/50 transition-all">
        <Search className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0" />
        <input
          data-testid="search-input"
          type="text"
          placeholder="Search players by gamertag..."
          className="bg-transparent text-sm text-white placeholder-gray-500 outline-none w-full"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { if (query.length >= 2) setOpen(true); }}
        />
        {query && (
          <button onClick={() => { setQuery(''); setResults([]); setOpen(false); }} className="text-gray-500 hover:text-white ml-1 flex-shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Dropdown results */}
      {open && query.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-[#1A1B22] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-[200] max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-[#1EF2FF]" />
            </div>
          )}

          {!loading && results.length === 0 && (
            <div className="text-center py-6 px-4">
              <p className="text-xs text-gray-500">No players found for "{query}"</p>
            </div>
          )}

          {!loading && results.map((player, i) => (
            <div
              key={player.id}
              data-testid={`search-result-${i}`}
              className="p-3 border-b border-white/[0.04] last:border-b-0 hover:bg-white/[0.04] transition-colors"
            >
              {/* Player info row */}
              <div className="flex items-center gap-3 mb-2.5">
                <div className={`w-10 h-10 rounded-full bg-gray-700/80 ring-2 ${getRankBorder(player.rank)} flex items-center justify-center text-sm font-bold text-white flex-shrink-0`}>
                  {(player.gamertag || 'U')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{player.gamertag}</p>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] font-semibold ${getRankColor(player.rank)}`}>{player.rank || 'Rookie'}</span>
                    {player.platform && <span className="text-[10px] text-gray-500 bg-white/5 px-1.5 py-0.5 rounded">{player.platform}</span>}
                  </div>
                </div>
                {/* Compact stats */}
                <div className="flex items-center gap-3 text-[10px] flex-shrink-0">
                  <div className="text-center">
                    <p className="font-bold text-green-400">{player.wins || 0}</p>
                    <p className="text-gray-600">W</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-red-400">{player.losses || 0}</p>
                    <p className="text-gray-600">L</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-[#1EF2FF]">{winRate(player.wins, player.losses)}</p>
                    <p className="text-gray-600">WR</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-[#FFD700]">{player.backer_count || 0}</p>
                    <p className="text-gray-600">BC</p>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-1.5">
                <button onClick={() => handleAction('challenge', player)} data-testid={`search-challenge-${i}`}
                  className="flex-1 flex items-center justify-center gap-1 h-8 bg-[#1EF2FF]/10 border border-[#1EF2FF]/20 rounded-lg text-[11px] font-semibold text-[#1EF2FF] hover:bg-[#1EF2FF]/20 transition-all active:scale-95">
                  <Swords className="w-3 h-3" />Challenge
                </button>
                <button onClick={() => handleAction('back', player)} data-testid={`search-back-${i}`}
                  className="flex-1 flex items-center justify-center gap-1 h-8 bg-[#FFD700]/10 border border-[#FFD700]/20 rounded-lg text-[11px] font-semibold text-[#FFD700] hover:bg-[#FFD700]/20 transition-all active:scale-95">
                  <Star className="w-3 h-3" />Back
                </button>
                <button onClick={() => handleAction('message', player)} data-testid={`search-message-${i}`}
                  className="flex-1 flex items-center justify-center gap-1 h-8 bg-white/5 border border-white/10 rounded-lg text-[11px] font-semibold text-gray-400 hover:bg-white/10 transition-all active:scale-95">
                  <MessageCircle className="w-3 h-3" />Message
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   FEATURED GAMES — Icons instead of emojis
   ───────────────────────────────────────────── */
const FEATURED_GAMES = [
  { name: 'FIFA 25', icon: Trophy, c: 'from-green-500/15 to-green-700/15', accent: 'text-green-400', border: 'border-green-500/20' },
  { name: 'Call of Duty', icon: Crosshair, c: 'from-orange-500/15 to-red-700/15', accent: 'text-orange-400', border: 'border-orange-500/20' },
  { name: 'NBA 2K25', icon: Target, c: 'from-blue-500/15 to-purple-700/15', accent: 'text-blue-400', border: 'border-blue-500/20' },
  { name: 'Madden 25', icon: Zap, c: 'from-red-500/15 to-red-800/15', accent: 'text-red-400', border: 'border-red-500/20' },
];

/* ─────────────────────────────────────────────
   AUTH CARD (Email-first flow — logic unchanged)
   ───────────────────────────────────────────── */
const AuthCard = ({ onAuthSuccess }) => {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [gamertag, setGamertag] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [userGamertag, setUserGamertag] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [platform, setPlatform] = useState('Mobile');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [birthDay, setBirthDay] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [country, setCountry] = useState('');
  const [currency, setCurrency] = useState('USD');
  
  const setAuth = useStore((state) => state.setAuth);
  const handleCountryChange = (e) => {
    const selected = e.target.value;
    setCountry(selected);
    setCurrency(selected === 'NG' ? 'NGN' : 'USD');
  };

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await BlishAPI.auth.checkEmail(email.trim());
      if (res.data.exists) {
        setUserGamertag(res.data.gamertag || '');
        setStep('password');
      } else {
        setStep('signup');
      }
    } catch {
      toast.error('Failed to check email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  
  const updateWallet = useStore((state) => state.updateWallet);
  const syncUserWallet = async () => {
    try {
      const { data } = await BlishAPI.wallet.getWallet();
      updateWallet({
        blish_crowns: data.blish_crowns,
        locked_crowns: data.locked_crowns
      });
    } catch (e) {
      console.error("Failed to sync wallet on login");
    }
  };
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // 1. Call the API
    const response = await BlishAPI.auth.login({ email, password });

    // 2. Decrypt the "payload" property returned by the Interceptor
    // The response looks like: { payload: "abc123encrypted..." }
    const decryptedData =decryptResponse(response.data.payload);

    console.log(decryptedData)
    // 3. Use the decrypted data (which is now your original JSON)
    setAuth(decryptedData.user, decryptedData.accessToken, decryptedData.refreshToken);
    
    await syncUserWallet();
    onAuthSuccess(decryptedData.user);
    toast.success('Welcome back!');
    } catch (err) {
      const msg = err.response?.data?.message || 'Login failed';
      if (err.response?.status === 401 && msg.includes('verify your email')) {
        setShowVerification(true);
        toast.error('Please verify your email first.');
      } else {
        toast.error(msg);
      }

      console.log(err)
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (!ageConfirmed) { toast.error('You must confirm you are 18+'); return; }
    if (!birthDay || !birthMonth || !birthYear) { toast.error('Please enter your date of birth'); return; }
    if (!country) { toast.error('Please select your country'); return; }


    
    setLoading(true);
    try {
        const dateOfBirth = `${birthYear}-${String(birthMonth).padStart(2, '0')}-${String(birthDay).padStart(2, '0')}`;
        const res = await BlishAPI.auth.register({
          gamertag, platform, email, password, confirm_password: confirmPassword,
          age_confirmed: ageConfirmed, date_of_birth: dateOfBirth,
          birth_day: birthDay, birth_month: birthMonth, birth_year: birthYear,
          country, currency
        });
      
        if (res.data.requires_verification) {
          setShowVerification(true);
          toast.success('Verification code sent to your email!');
        } else {
          
          setAuth(res.data.user, res.data.accessToken, res.data.refreshToken);
          onAuthSuccess(res.data.user);
        }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await BlishAPI.auth.verifyEmail({ email, code: verificationCode });
      setAuth(res.data.user, res.data.accessToken, res.data.refreshToken);
      // ✅ Sync wallet if available
      if (res.data.accessToken) {
        await syncUserWallet();
      }
      
      onAuthSuccess(res.data.user);
      toast.success('Email verified! Welcome!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // const redirectUrl = window.location.origin;
    // window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

if (showVerification) {
  return (
    <div data-testid="auth-card-verify" className="w-full bg-[#1A1B22]/90 backdrop-blur-xl border border-white/10 border-t-2 border-t-[#1EF2FF] p-6 sm:p-8 rounded-2xl shadow-[0_0_60px_rgba(0,0,0,0.9)]">
      <div className="text-center mb-6">
        <div className="w-14 h-14 bg-[#1EF2FF]/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <Crown className="w-7 h-7 text-[#1EF2FF]" />
        </div>
        <h2 className="text-xl font-black text-white tracking-tight">VERIFY EMAIL</h2>
        <p className="text-sm text-gray-400 mt-1">Code sent to <span className="text-[#1EF2FF]">{email}</span></p>
      </div>
      <form onSubmit={handleVerify} className="space-y-4">
        <Input data-testid="auth-verify-code-input" type="text" maxLength={6} placeholder="000000"
          className="bg-white/5 border-white/10 text-white text-center text-2xl font-mono tracking-widest h-14 rounded-lg"
          value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
        <Button data-testid="auth-verify-submit-btn" type="submit" disabled={loading || verificationCode.length !== 6}
          className="w-full bg-[#1EF2FF] hover:bg-[#1EF2FF]/80 text-black font-bold h-12 rounded-lg transition-transform active:scale-95">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'VERIFY'}
        </Button>
      </form>

      {/* ── RESEND BUTTON ── */}
      <ResendVerification email={email} />
    </div>
  );
}

  return (
    <div data-testid="auth-card" className={`w-full bg-[#1A1B22]/90 backdrop-blur-xl border border-white/10 border-t-2 border-t-[#1EF2FF] rounded-2xl shadow-[0_0_60px_rgba(0,0,0,0.9)] relative overflow-hidden ${step === 'signup' ? 'p-6 sm:p-8' : 'p-5 sm:p-6'}`}>
      <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#1EF2FF]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-[#9B45FF]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10">
        <div className={`text-center ${step === 'signup' ? 'mb-6' : 'mb-4'}`}>
          <Crown className={`text-[#FFD700] mx-auto mb-1.5 ${step === 'signup' ? 'w-10 h-10' : 'w-8 h-8'}`} style={{ filter: 'drop-shadow(0 0 15px rgba(255,215,0,0.6))' }} />
          <h2 className="text-lg font-black text-white tracking-tight">
            {step === 'email' ? 'ENTER THE ARENA' : step === 'password' ? 'WELCOME BACK' : 'CLAIM YOUR CROWN'}
          </h2>
          {step === 'password' && userGamertag && (
            <p className="text-sm text-[#1EF2FF] mt-0.5 font-semibold">{userGamertag}</p>
          )}
          {step === 'email' && (
            <p className="text-xs text-gray-400 mt-0.5">Compete. Win. Earn Real Rewards.</p>
          )}
        </div>

        {/* {step === 'email' && (
          <Button data-testid="auth-google-btn" type="button" onClick={handleGoogleLogin}
            className="w-full mb-3 h-11 bg-white hover:bg-gray-100 text-gray-800 font-semibold flex items-center justify-center gap-2 rounded-lg transition-all text-sm">
            <GoogleIcon /> Continue with Google
          </Button>
        )}

        {step === 'email' && (
          <div className="relative mb-3">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center text-xs">
              <span className="px-3 bg-[#1A1B22] text-gray-500">or with email</span>
            </div>
          </div>
        )} */}

        <AnimatePresence mode="wait">
          {step === 'email' && (
            <motion.form key="email" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} onSubmit={handleCheckEmail} className="space-y-3">
              <Input data-testid="auth-email-input" type="email" placeholder="your@email.com"
                className="bg-white/5 border-white/10 text-white placeholder-gray-500 rounded-lg h-11 focus:border-[#1EF2FF]"
                value={email} onChange={e => setEmail(e.target.value)} required />
              <Button data-testid="auth-email-submit-btn" type="submit" disabled={loading}
                className="w-full bg-[#1EF2FF] hover:bg-[#1EF2FF]/80 text-black font-bold h-11 rounded-lg transition-transform active:scale-95">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><span>CONTINUE</span><ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </motion.form>
          )}
          {step === 'password' && (
            <motion.form key="password" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} onSubmit={handleLogin} className="space-y-3">
              <div className="relative">
                <Input data-testid="auth-password-input" type={showPwd ? 'text' : 'password'} placeholder="Password"
                  className="bg-white/5 border-white/10 text-white placeholder-gray-500 rounded-lg h-11 focus:border-[#1EF2FF] pr-10"
                  value={password} onChange={e => setPassword(e.target.value)} required />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button data-testid="auth-login-submit-btn" type="submit" disabled={loading}
                className="w-full bg-[#1EF2FF] hover:bg-[#1EF2FF]/80 text-black font-bold h-11 rounded-lg transition-transform active:scale-95">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Crown className="w-4 h-4 mr-2" /><span>ENTER ARENA</span></>}
              </Button>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={() => { setStep('email'); setPassword(''); }} className="text-gray-400 hover:text-white transition-colors">Change email</button>
                <Link to="/forgot-password" className="text-[#FFD700] hover:underline">Forgot password?</Link>
              </div>
            </motion.form>
          )}
          {step === 'signup' && (
            <motion.form key="signup" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} onSubmit={handleSignup} className="space-y-3">
              <p className="text-xs text-gray-400 text-center mb-2">No account found for <span className="text-[#1EF2FF]">{email}</span></p>
              <Input data-testid="auth-gamertag-input" type="text" placeholder="Gamertag" minLength={3} maxLength={20}
                className=" bg-gray-800 border-white/10 text-white placeholder-gray-500 rounded-lg h-12 focus:border-[#1EF2FF]"
                value={gamertag} onChange={e => setGamertag(e.target.value)} required />
              <select data-testid="auth-platform-select"
                className="w-full  bg-gray-800 border border-white/10 text-white rounded-lg h-12 px-3 focus:border-[#1EF2FF] focus:outline-none"
                value={platform} onChange={e => setPlatform(e.target.value)}>
                <option value="Mobile">Mobile</option>
                <option value="PC">PC</option>
                <option value="Console">Console</option>
              </select>
              <div className="grid grid-cols-3 gap-2">
                <select data-testid="auth-birth-day" className=" bg-gray-800 border border-white/10 text-white rounded-lg h-12 px-2 text-sm focus:border-[#1EF2FF] focus:outline-none" value={birthDay} onChange={e => setBirthDay(e.target.value)} required>
                  <option value="">Day</option>
                  {[...Array(31)].map((_, i) => <option key={i+1} value={i+1}>{i+1}</option>)}
                </select>
                <select data-testid="auth-birth-month" className=" bg-gray-800 border border-white/10 text-white rounded-lg h-12 px-2 text-sm focus:border-[#1EF2FF] focus:outline-none" value={birthMonth} onChange={e => setBirthMonth(e.target.value)} required>
                  <option value="">Month</option>
                  {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
                <select data-testid="auth-birth-year" className=" bg-gray-800 border border-white/10 text-white rounded-lg h-12 px-2 text-sm focus:border-[#1EF2FF] focus:outline-none" value={birthYear} onChange={e => setBirthYear(e.target.value)} required>
                  <option value="">Year</option>
                  {[...Array(80)].map((_, i) => { const y = new Date().getFullYear() - 18 - i; return <option key={y} value={y}>{y}</option>; })}
                </select>
              </div>
              {/* Country */}
                <div className="relative">
                  <select
                    data-testid="auth-country-select"
                    className="w-full  bg-gray-800 border border-white/10  rounded-lg h-12 px-3 focus:border-[#1EF2FF] focus:outline-none appearance-none"
                    value={country}
                    onChange={handleCountryChange}
                    required
                  >
                    <option value="" disabled>Select country</option>
                    <option value="NG">🇳🇬 Nigeria</option>
                    <option value="US">🇺🇸 United States</option>
                    <option value="GB">🇬🇧 United Kingdom</option>
                    <option value="CA">🇨🇦 Canada</option>
                    <option value="AU">🇦🇺 Australia</option>
                    <option value="GH">🇬🇭 Ghana</option>
                    <option value="KE">🇰🇪 Kenya</option>
                    <option value="ZA">🇿🇦 South Africa</option>
                    <option value="IN">🇮🇳 India</option>
                    <option value="DE">🇩🇪 Germany</option>
                    <option value="FR">🇫🇷 France</option>
                    <option value="BR">🇧🇷 Brazil</option>
                    <option value="AE">🇦🇪 UAE</option>
                    <option value="OTHER">🌍 Other</option>
                  </select>
                  <Globe className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                </div>
                
                {/* Currency — auto-set, but user can override */}
                <div className="flex items-center gap-2">
                  <div className={`flex-1 flex items-center gap-2 px-3 h-12 rounded-lg border transition-all ${
                    currency === 'NGN'
                      ? 'bg-green-500/10 border-green-500/40'
                      : 'bg-[#1EF2FF]/10 border-[#1EF2FF]/40'
                  }`}>
                    <CircleDollarSign className={`w-4 h-4 flex-shrink-0 ${currency === 'NGN' ? 'text-green-400' : 'text-[#1EF2FF]'}`} />
                    <span className="text-xs text-gray-400 flex-1">Currency</span>
                    <span className={`text-sm font-black tracking-wider ${currency === 'NGN' ? 'text-green-400' : 'text-[#1EF2FF]'}`}>
                      {currency}
                    </span>
                  </div>
                  <select
                    data-testid="auth-currency-select"
                    className="bg-white/5 border border-white/10 text-white rounded-lg h-12 px-3 focus:border-[#1EF2FF] focus:outline-none text-sm font-semibold"
                    value={currency}
                    onChange={e => setCurrency(e.target.value)}
                  >
                    <option value="USD">USD — Dollar</option>
                    <option value="NGN">NGN — Naira</option>
                  </select>
                </div>
              <div className="relative">
                <Input data-testid="auth-signup-password-input" type={showPwd ? 'text' : 'password'} placeholder="Password (8+ chars)"
                  className="bg-white/5 border-white/10 text-white placeholder-gray-500 rounded-lg h-12 focus:border-[#1EF2FF] pr-10"
                  value={password} onChange={e => setPassword(e.target.value)} required minLength={8} />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              
              <Input data-testid="auth-confirm-password-input" type="password" placeholder="Confirm password"
                className="bg-white/5 border-white/10 text-white placeholder-gray-500 rounded-lg h-12 focus:border-[#1EF2FF]"
                value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
              <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all text-sm ${ageConfirmed ? 'bg-[#1EF2FF]/10 border-[#1EF2FF]/50 text-white' : 'bg-white/5 border-white/10 text-gray-400'}`}>
                <input data-testid="auth-age-confirm" type="checkbox" checked={ageConfirmed} onChange={e => setAgeConfirmed(e.target.checked)} className="accent-[#1EF2FF]" />
                I confirm I am 18+ and accept the <Link to="/terms" className="text-[#1EF2FF] hover:underline">terms</Link>
              </label>
              <Button data-testid="auth-signup-submit-btn" type="submit" disabled={loading || !ageConfirmed}
                className="w-full bg-[#1EF2FF] hover:bg-[#1EF2FF]/80 text-black font-bold h-12 rounded-lg transition-transform active:scale-95">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'CREATE ACCOUNT'}
              </Button>
              <button type="button" onClick={() => { setStep('email'); setPassword(''); setConfirmPassword(''); }} className="w-full text-center text-xs text-gray-400 hover:text-white transition-colors">Change email</button>
            </motion.form>
          )}
        </AnimatePresence>

        <div className={`${step === 'signup' ? 'mt-5 pt-4' : 'mt-3 pt-3'} border-t border-white/5 text-center`}>
          <p className="text-[10px] text-gray-500">18+ Only &middot; Skill-Based Competition &middot; <Link to="/terms" className="text-gray-400 hover:text-white">Terms</Link></p>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN LANDING PAGE COMPONENT
   ═══════════════════════════════════════════════ */
export default function LandingBrandKit() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [challenges, setChallenges] = useState([]);
  const [matchups, setMatchups] = useState([]);
  const [stats, setStats] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const authRef = useRef(null);

const accessToken = useStore((state) => state.accessToken);
const setAuth = useStore((state) => state.setAuth);

useEffect(() => {
  const token = accessToken;
  if (token) navigate('/dashboard', { replace: true });
}, [navigate, accessToken]);


  useEffect(() => {
    const handleCallback = async () => {
      const sessionId = searchParams.get('session_id');
      if (!sessionId) return;
      try {
        const res = await BlishAPI.auth.googleSession({ session_id: sessionId });
        setAuth(res.data.user, res.data.token, null);
        toast.success(res.data.is_new_user ? 'Welcome to Blishcrown!' : 'Welcome back!');
        navigate('/dashboard');
      } catch {
        toast.error('Google authentication failed');
      }
    };
    handleCallback();
  }, [searchParams, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cRes, mRes, sRes] = await Promise.all([
          BlishAPI.public.getLandingChallenges().catch(() => ({ data: [] })),
          BlishAPI.public.getMatchups().catch(() => ({ data: [] })),
          BlishAPI.public.getLandingStats().catch(() => ({ data: null })),
        ]);
        setChallenges(cRes.data.length > 0 ? cRes.data : MOCK_CHALLENGES);
        setMatchups(mRes.data.length > 0 ? mRes.data : MOCK_MATCHUPS);
        setStats(sRes.data);
      } catch {
        setChallenges(MOCK_CHALLENGES);
        setMatchups(MOCK_MATCHUPS);
      }
    };
    fetchData();
  }, []);

  const handleAuthSuccess = (user) => {
    navigate('/dashboard');
  };

  const handleSearchAction = useCallback((action, player) => {
    toast.info(`Sign in to ${action} ${player.gamertag}!`);
    if (authRef.current) {
      authRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const promptAuth = useCallback(() => {
    toast.info('Sign in to access this feature!');
    if (authRef.current) {
      authRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const handleTabClick = useCallback((key) => {
    setActiveTab(key);
    if (key === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    promptAuth();
  }, [promptAuth]);

  const handleMenuNavClick = useCallback(() => {
    promptAuth();
  }, [promptAuth]);

  const liveCount = matchups.filter(m => m.status === 'active').length;

  return (
    <div className="h-screen w-full overflow-hidden flex flex-col bg-[#0D0E13] text-white relative" data-testid="landing-page">

      <BackgroundCarousel />

      {/* ── HEADER ── */}
      <header data-testid="landing-header" className="flex-shrink-0 w-full backdrop-blur-xl bg-[#0D0E13]/70 border-b border-white/10 px-4 py-3 flex items-center justify-between z-50 relative">
        <LogoPrimary size="sm" />

        <div className="hidden md:block w-full max-w-md mx-6">
          <SearchDropdown onAction={handleSearchAction} />
        </div>

        <div className="flex items-center gap-3">
          {stats && (
            <span className="hidden lg:flex items-center gap-1.5 text-xs text-gray-400 mr-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              {stats.total_players || 0} Online
            </span>
          )}
          <Link to="/risk-disclosure" className="text-xs text-gray-400 hover:text-white transition-colors hidden md:block">Risk Disclosure</Link>
        </div>
      </header>

      <TrustBar />

      {/* ── MAIN 3-COLUMN GRID ── */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[340px_1fr_340px] xl:grid-cols-[420px_1fr_420px] overflow-hidden relative z-10">

        {/* ── LEFT COLUMN ── */}
        <div data-testid="left-column-challenges" className="hidden lg:flex flex-col overflow-y-auto hide-scrollbar border-r border-white/10">
          <div className="p-6 pb-4 border-b border-white/5">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <h1 className="text-2xl xl:text-3xl font-black text-white leading-tight tracking-tight">
                The Digital{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-[#FFA500]">Boardman</span>
              </h1>
              <p className="text-sm text-gray-400 mt-2 leading-relaxed">Show your skills globally from your couch.</p>
            </motion.div>
          </div>

          <div className="sticky top-0 bg-[#0D0E13]/80 backdrop-blur-md z-10 px-4 py-3 border-b border-white/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#9B45FF]" />
                <h3 className="text-xs font-bold text-white uppercase tracking-wider">Open Challenges</h3>
              </div>
              <span className="text-[10px] text-gray-500">{challenges.length} available</span>
            </div>
          </div>
          <div className="p-3 space-y-2.5 flex-1">
            {challenges.map((c, i) => (
              <ChallengeCard key={c.id || i} challenge={c} index={i} onAction={promptAuth} />
            ))}
            <div className="text-center py-6">
              <p className="text-xs text-gray-500">More challenges available after sign in</p>
            </div>
          </div>
        </div>

        {/* ── CENTER COLUMN ── */}
        <div data-testid="center-column-auth" className="flex flex-col items-center p-4 sm:p-6 lg:px-4 relative overflow-y-auto pb-24 lg:pb-8">

          {/* Mobile search */}
          <div className="md:hidden w-full max-w-md mb-5">
            <SearchDropdown onAction={handleSearchAction} />
          </div>

          {/* Mobile hero */}
          <div className="lg:hidden w-full max-w-md mb-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
              <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight tracking-tight">
                The Digital{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-[#FFA500]">Boardman</span>
              </h1>
              <p className="text-sm sm:text-base text-gray-400 mt-2">Show your skills globally from your couch.</p>
            </motion.div>
          </div>

          {/* Platform badges */}
          <div className="flex items-center gap-2 mb-6">
            {['PS5', 'Xbox', 'PC', 'Mobile'].map(p => (
              <span key={p} className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-[10px] text-gray-400 font-semibold uppercase tracking-wider hover:border-[#1EF2FF]/30 transition-colors">
                {p}
              </span>
            ))}
          </div>

          {/* Auth Card */}
          <div ref={authRef} className="w-full">
            <AuthCard onAuthSuccess={handleAuthSuccess} />
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
              <Shield className="w-3 h-3 text-green-400" /> Anti-Cheat Protected
            </span>
            <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
              <Trophy className="w-3 h-3 text-[#FFD700]" /> Skill-Based Only
            </span>
            <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
              <TrendingUp className="w-3 h-3 text-[#1EF2FF]" /> Real Rewards
            </span>
          </div>

          {/* How It Works Section */}
          <div className="mt-8 w-full max-w-md lg:max-w-none">
            <HowItWorks />
          </div>

          {/* Mobile: Featured games */}
          <div className="lg:hidden mt-8 w-full max-w-md">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-white/10" />
              <h3 className="text-xs sm:text-sm font-bold text-gray-300 uppercase tracking-widest whitespace-nowrap">Featured Games</h3>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-white/10" />
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {FEATURED_GAMES.map((g, i) => (
                <motion.div
                  key={g.name}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + i * 0.08 }}
                  className={`bg-gradient-to-br ${g.c} border ${g.border} rounded-xl p-4 text-center hover:scale-[1.02] transition-transform`}
                >
                  <g.icon className={`w-6 h-6 mx-auto mb-1.5 ${g.accent}`} />
                  <p className="text-xs font-bold text-white">{g.name}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Mobile: Matchups + Challenges */}
          <div className="lg:hidden mt-8 w-full max-w-md space-y-3">
            <div className="flex items-center justify-center gap-2 mb-1">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-white/10" />
              <h3 className="text-xs sm:text-sm font-bold text-gray-300 uppercase tracking-widest flex items-center gap-1.5 whitespace-nowrap">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                Live Matchups
              </h3>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-white/10" />
            </div>
            {matchups.slice(0, 3).map((m, i) => (
              <MatchupCard key={m.id || i} matchup={m} index={i} onAction={promptAuth} />
            ))}

            <div className="flex items-center justify-center gap-2 pt-3 mb-1">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-white/10" />
              <h3 className="text-xs sm:text-sm font-bold text-gray-300 uppercase tracking-widest whitespace-nowrap">Open Challenges</h3>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-white/10" />
            </div>
            {challenges.slice(0, 3).map((c, i) => (
              <ChallengeCard key={c.id || i} challenge={c} index={i} onAction={promptAuth} />
            ))}
          </div>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div data-testid="right-column-matchups" className="hidden lg:flex flex-col overflow-y-auto hide-scrollbar border-l border-white/10">
          <div className="sticky top-0 bg-[#0D0E13]/80 backdrop-blur-md z-10 p-4 pb-3 border-b border-white/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Swords className="w-4 h-4 text-[#FFD700]" />
                <h3 className="text-xs font-bold text-white uppercase tracking-wider">Live Matchups</h3>
              </div>
              <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                {liveCount} Live
              </span>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">Player vs Player battles happening now</p>
          </div>
          <div className="p-3 space-y-2.5 flex-1">
            {matchups.map((m, i) => (
              <MatchupCard key={m.id || i} matchup={m} index={i} onAction={promptAuth} />
            ))}
            <div className="text-center py-6">
              <p className="text-xs text-gray-500">Sign in to watch & bet on matches</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── DESKTOP FOOTER ── */}
      <footer data-testid="landing-footer" className="hidden lg:flex flex-shrink-0 w-full bg-[#0D0E13]/90 backdrop-blur-md border-t border-white/10 px-4 py-3 flex-wrap items-center justify-center gap-x-6 gap-y-1 text-xs text-gray-500 z-50 relative">
        <Link to="/features" className="hover:text-white transition-colors">Features</Link>
        <Link to="/how-it-works" className="hover:text-white transition-colors">How It Works</Link>
        <Link to="/faq" className="hover:text-white transition-colors">FAQ</Link>
        <Link to="/about" className="hover:text-white transition-colors">About</Link>
        <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
        <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
        <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
        <Link to="/risk-disclosure" className="hover:text-white transition-colors">Risk Disclosure</Link>
        <a href="https://discord.gg/Tbca3Hvd4w" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Discord</a>
        <span className="text-gray-600">&copy; 2025 Blishcrown</span>
      </footer>

      <MobileBottomNav onTabClick={handleTabClick} onMenuClick={() => setShowMenu(true)} activeTab={activeTab} />
      <MobileMenuOverlay show={showMenu} onClose={() => setShowMenu(false)} onNavClick={handleMenuNavClick} />
    </div>
  );
}
